<script>
import { nanoid } from 'nanoid'
import { mapGetters, mapActions } from 'vuex'
import baseMixin from '@/mixins/baseMixin'
import $dayjs from '@/services/date'
import ClientDetailsLink from '@components/general/client-details-link.vue'
import SubBubbleAgreementDiscountViewModel from '@/models/subBubbleAgreement/subBubbleAgreementDiscountViewModel'
import ClientPayClassClassificationViewModel from '@/models/client/clientPayClassClassificationViewModel'
import SubBubbleAgreementsEditorDiscountRow from './sub-bubble-agreement-editor-discount-row.vue'

export default {
  name: 'SubBubbleAgreementsEditorDialog',
  components: {
    SubBubbleAgreementsEditorDiscountRow,
    ClientDetailsLink,
  },
  mixins: [baseMixin],
  props: {
    /**
     * @param {SubBubbleAgreementViewModel} subBubbleAgreement
     */
    subBubbleAgreement: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    isSaving: {
      type: Boolean,
      required: true,
    },
  },
  data: () => {
    return {
      /**
       * @type {SubBubbleAgreementViewModel}
       */
      form: null,
      isValid: true,
      startDatePickerMenu: false,
      endDatePickerMenu: false,
      rules: {},
      /**
       * @type {ClientPayClassClassificationViewModel[]}
       */
      classifications: [],
      originalFormSerialised: null, // Used to keep track of whether the form is dirty
    }
  },
  computed: {
    ...mapGetters({
      returnUserTimeformat: 'auth/currentUserDateFormat',
    }),
    isGroupLevelAgreement() {
      return !!this.subBubbleAgreement.clientGroupId
    },
    startDateFormatted() {
      return this.localiseDate(this.form.startDateLocal)
    },
    endDateFormatted() {
      return this.localiseDate(this.form.endDateLocal)
    },
    availableClassifications() {
      const classifications = [...this.classifications]

      if (this.form.discounts.length === 1) {
        classifications.unshift(this.constructDefaultClassification())
      }

      return classifications
    },
    onlyDefaultDiscountPresent() {
      return (
        this.form.discounts.length === 1 &&
        this.form.discounts[0].classificationId === null
      )
    },
    canAddDiscount() {
      return (
        !this.onlyDefaultDiscountPresent &&
        this.form.discounts.length < this.maxDiscountCount
      )
    },
    maxDiscountCount() {
      return this.classifications.length
    },
    classificationIdsInUse() {
      return this.form.discounts.map((c) => c.classificationId)
    },
    isStartDateAfterEndDate() {
      return this.form.startDateLocal > this.form.endDateLocal
    },
    isEndDateBeforeStartDate() {
      return this.form.endDateLocal < this.form.startDateLocal
    },
    saveBtnText() {
      return this.isSaving
        ? this.$t('general.savingBtnLabel')
        : this.$t('general.save')
    },
    isFormDirty() {
      return this.originalFormSerialised !== JSON.stringify(this.form)
    },
  },
  watch: {
    subBubbleAgreement: {
      immediate: true,
      deep: true,
      handler(subBubbleAgreement) {
        this.loadAvailableClassifications()
        this.createLocalForm(subBubbleAgreement)
      },
    },
  },
  created() {
    this.initValidationRules()
  },
  methods: {
    ...mapActions({
      getPayClassClassificationsByClientId:
        'client/getPayClassClassificationsByClientId',
      getPayClassClassificationsByClientGroupId:
        'client/getPayClassClassificationsByClientGroupId',
    }),
    initValidationRules() {
      this.rules = {
        startDateAfterEnd: () =>
          this.form
            ? !this.isStartDateAfterEndDate ||
              this.$t('input.startDateBeforeEndValidationText')
            : true,
        endDateBeforeStart: () =>
          this.form
            ? !this.isEndDateBeforeStartDate ||
              this.$t('input.endDateBeforeStartValidationText')
            : true,
      }
    },
    async loadAvailableClassifications() {
      const response = this.isGroupLevelAgreement
        ? await this.getPayClassClassificationsByClientGroupId(
            this.subBubbleAgreement.clientGroupId
          )
        : await this.getPayClassClassificationsByClientId(
            this.subBubbleAgreement.clientId
          )

      if (response.isSuccess) this.classifications = response.data
    },
    closeDialog() {
      this.$emit('close-dialog')
    },
    validate() {
      return this.$refs.subBubbleAgreementsEditorForm.validate()
    },
    save() {
      this.isValid = this.validate()
      if (!this.isValid) return

      this.$emit('on-save', this.form)
    },
    createLocalForm(subBubbleAgreement) {
      const formSerialised = JSON.stringify(subBubbleAgreement)
      this.form = JSON.parse(formSerialised)
      this.originalFormSerialised = formSerialised
    },
    constructDefaultClassification() {
      return new ClientPayClassClassificationViewModel({
        classificationId: null,
        classification: this.$t('subBubbleAgreements.allClassificationsText'),
      })
    },
    addNewClassificationDiscount() {
      this.form.discounts.push(
        new SubBubbleAgreementDiscountViewModel({
          id: nanoid(),
          subBubbleAgreementId: this.form.id,
          isClassificationDiscount: true,
        })
      )
    },
    onDiscountUpdated(discount) {
      const foundIndex = this.form.discounts.findIndex(
        (x) => x.id === discount.id
      )
      if (foundIndex > -1) this.form.discounts.splice(foundIndex, 1, discount)
    },
    deleteDiscount(discount) {
      const foundIndex = this.form.discounts.findIndex(
        (d) => d.id === discount.id
      )
      if (foundIndex > -1) this.form.discounts.splice(foundIndex, 1)
    },
    localiseDate(val) {
      return $dayjs(val).format(this.returnUserTimeformat)
    },
    onStartDateChanged(val) {
      // Set to start of day
      this.form.startDateLocal = $dayjs(val).format('YYYY-MM-DDT00:00:00')
    },
    onEndDateChanged(val) {
      // Set to end of day
      this.form.endDateLocal = $dayjs(val).format('YYYY-MM-DDT23:59:59')
    },
  },
}
</script>

<template>
  <v-dialog
    :value="true"
    max-width="1000px"
    scrollable
    @input="(v) => v || closeDialog()"
  >
    <v-card data-testid="sub-bubble-agreements-editor-dialog-content">
      <v-card-title>
        <span class="text-h5 mb-1" data-testid="title">{{ title }}</span>
      </v-card-title>
      <v-card-subtitle>
        {{ $t('subBubbleAgreements.subBubbleAgreementIsForLabel') }}
        <ClientDetailsLink
          :client-id="subBubbleAgreement.clientId"
          :group-id="subBubbleAgreement.clientGroupId"
        />
      </v-card-subtitle>
      <v-card-text>
        <v-form
          ref="subBubbleAgreementsEditorForm"
          lazy-validation
          :value="isValid"
        >
          <v-row class="mt-3">
            <v-col cols="12" md="3">
              <v-menu
                ref="startDatePickerMenu"
                v-model="startDatePickerMenu"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="startDateFormatted"
                    data-testid="start-date-formatted"
                    :label="$t('subBubbleAgreements.input.startDateLabel')"
                    prepend-icon="mdi-calendar"
                    v-bind="attrs"
                    :rules="[rules.startDateAfterEnd]"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  :value="form.startDateLocal"
                  no-title
                  @change="onStartDateChanged"
                ></v-date-picker>
              </v-menu>
              <v-menu
                ref="endDatePickerMenu"
                v-model="endDatePickerMenu"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="endDateFormatted"
                    data-testid="end-date-formatted"
                    :label="$t('subBubbleAgreements.input.endDateLabel')"
                    prepend-icon="mdi-calendar"
                    :rules="[rules.endDateBeforeStart]"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  :value="form.endDateLocal"
                  no-title
                  @change="onEndDateChanged"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col>
              <h4 class="d-inline-block">{{
                $t('subBubbleAgreements.classificationDiscountsTitle')
              }}</h4>
              <v-tooltip right max-width="400">
                <template v-slot:activator="{ on }">
                  <v-icon small color="info" class="ml-1" v-on="on"
                    >mdi-help-circle</v-icon
                  >
                </template>
                <span>{{
                  $t('subBubbleAgreements.classificationDiscountsTooltip')
                }}</span>
              </v-tooltip>

              <SubBubbleAgreementsEditorDiscountRow
                v-for="discount in form.discounts"
                :key="discount.id"
                :value="discount"
                :classifications="availableClassifications"
                :classification-ids-in-use="classificationIdsInUse"
                @input="onDiscountUpdated"
                @on-delete="deleteDiscount(discount)"
              />

              <a
                v-if="canAddDiscount"
                data-testid="add-discount-btn"
                href="#"
                @click.prevent.stop="addNewClassificationDiscount"
                >{{
                  $t('subBubbleAgreements.addClassificationDiscountBtnText')
                }}</a
              >

              <span data-testid="add-discount-btn-container"> </span>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="closeDialog">
          {{ $t('general.close') }}
        </v-btn>
        <v-btn
          color="blue darken-1"
          text
          :disabled="!isFormDirty"
          :loading="isSaving"
          @click="save"
        >
          {{ saveBtnText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
